import React, { useEffect, useRef, useState } from "react";
import { Button, Typography, Divider } from "antd";

const { Text, Title } = Typography;

const CANVAS_W = 900;
const CANVAS_H = 740;
const CAM_W = 160;
const CAM_H = 120;

export default function SpaceDodgeGame() {
  const videoRef = useRef(null);
  const camCanvasRef = useRef(null);
  const gameCanvasRef = useRef(null);
  const rafRef = useRef(null);
  const camRafRef = useRef(null);
  const streamRef = useRef(null);
  const runningRef = useRef(false);
  const previousFrameRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [status, setStatus] = useState("Ready – Click Start");
  const [debugInfo, setDebugInfo] = useState("");

  const gameRef = useRef({
    width: CANVAS_W,
    height: CANVAS_H,
    ship: {
      x: CANVAS_W / 2,
      y: CANVAS_H - 80,
      w: 44,
      h: 28,
      vx: 0,
      vy: 0,
      speed: 1.0,
      maxSpeed: 12,
      friction: 0.95,
      cooldown: 0,
    },
    bullets: [],
    asteroids: [],
    lastAsteroidAt: 0,
    time: 0,
    shake: { x: 0, y: 0, intensity: 0, duration: 0 },
    gameOver: false,
  });

  const gestureRef = useRef({
    moveLeft: false,
    moveRight: false,
    shoot: false,
    brightPixelCount: 0,
    boost: false,
  });

  useEffect(() => {
    drawInitialState();

    return () => {
      stopGame();
    };
  }, []);

  function drawInitialState() {
    if (!gameCanvasRef.current) return;
    const ctx = gameCanvasRef.current.getContext("2d");
    ctx.fillStyle = "#051526";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const ship = gameRef.current.ship;
    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.fillStyle = "#7ee787";
    ctx.beginPath();
    ctx.moveTo(ship.w / 2, 0);
    ctx.lineTo(ship.w, ship.h);
    ctx.lineTo(0, ship.h);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#022b32";
    ctx.beginPath();
    ctx.arc(ship.w / 2, ship.h * 0.6, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    if (!running) {
      ctx.fillStyle = "#fff";
      ctx.font = "bold 32px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Click Start Game to begin", CANVAS_W / 2, CANVAS_H / 6);
    }
  }

  function triggerShake(intensity = 5, duration = 200) {
    const shake = gameRef.current.shake;
    shake.intensity = intensity;
    shake.duration = duration;
  }

  /* ---------------------- Motion & Brightness Detection Logic ---------------------- */
  function detectHandGestures(video) {
    if (!video || video.readyState < 2) return;

    try {
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = CAM_W;
      tempCanvas.height = CAM_H;
      const ctx = tempCanvas.getContext("2d");

      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(video, -CAM_W, 0, CAM_W, CAM_H);
      ctx.restore();

      const imageData = ctx.getImageData(0, 0, CAM_W, CAM_H);
      const data = imageData.data;

      if (!previousFrameRef.current) {
        previousFrameRef.current = new Uint8ClampedArray(data);
        return;
      }

      const prevData = previousFrameRef.current;

      let leftMotion = 0;
      let rightMotion = 0;
      let brightPixels = 0;

      const MOTION_DIFF_THRESHOLD = 20;
      const MOVEMENT_PIXEL_THRESHOLD = 100;
      const BRIGHTNESS_THRESHOLD = 150;
      const SHOOT_BRIGHTNESS_THRESHOLD = 500;

      for (let y = 0; y < CAM_H; y++) {
        for (let x = 0; x < CAM_W; x++) {
          const i = (y * CAM_W + x) * 4;

          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          if ((r + g + b) / 3 > BRIGHTNESS_THRESHOLD) {
            brightPixels++;
          }

          const rDiff = Math.abs(r - prevData[i]);
          const gDiff = Math.abs(g - prevData[i + 1]);
          const bDiff = Math.abs(b - prevData[i + 2]);
          const diff = (rDiff + gDiff + bDiff) / 3;

          if (diff > MOTION_DIFF_THRESHOLD) {
            if (x < CAM_W / 3) {
              leftMotion++;
            } else if (x > (2 * CAM_W) / 3) {
              rightMotion++;
            }
          }
        }
      }

      previousFrameRef.current = new Uint8ClampedArray(data);

      const g = gestureRef.current;

      g.moveLeft = leftMotion > MOVEMENT_PIXEL_THRESHOLD;
      g.moveRight = rightMotion > MOVEMENT_PIXEL_THRESHOLD;
      g.shoot = brightPixels > SHOOT_BRIGHTNESS_THRESHOLD;
      g.brightPixelCount = brightPixels;

      g.boost = g.moveLeft || g.moveRight;

      setDebugInfo(
        `L Motion:${leftMotion} R Motion:${rightMotion} Bright Pixels: ${brightPixels}`
      );
    } catch (e) {
      console.error("Detection error:", e);
    }
  }

  /* ---------------------- Game Logic ---------------------- */
  function spawnAsteroid(baseSpeed) {
    const size = 20 + Math.random() * 36;
    gameRef.current.asteroids.push({
      x: Math.random() * (CANVAS_W - size),
      y: -size,
      vy: baseSpeed + Math.random() * 2.0,
      size,
    });
  }

  function step(dt) {
    if (!runningRef.current) return;

    const g = gameRef.current;
    g.time += dt;

    // Update shake
    if (g.shake.duration > 0) {
      g.shake.duration -= dt;
      const intensity = g.shake.intensity * (g.shake.duration / 200);
      g.shake.x = (Math.random() - 0.5) * intensity * 2;
      g.shake.y = (Math.random() - 0.5) * intensity * 2;
    } else {
      g.shake.x = 0;
      g.shake.y = 0;
      g.shake.intensity = 0;
    }

    const difficultyLevel = Math.floor(score / 50);

    const maxDelay = 700;
    const minDelay = 500;
    const delayReductionPerLevel = 75;
    const spawnDelay = Math.max(
      minDelay,
      maxDelay - difficultyLevel * delayReductionPerLevel
    );

    if (g.time - g.lastAsteroidAt > spawnDelay) {
      const baseAsteroidSpeed = 2.0 + difficultyLevel * 0.8;
      spawnAsteroid(baseAsteroidSpeed);
      g.lastAsteroidAt = g.time;
    }

    const ship = g.ship;
    const { moveLeft, moveRight, shoot } = gestureRef.current;

    const dt_factor = dt / 16.6;

    if (moveLeft && !moveRight) {
      ship.vx = Math.max(-ship.maxSpeed, ship.vx - ship.speed * dt_factor);
    } else if (moveRight && !moveLeft) {
      ship.vx = Math.min(ship.maxSpeed, ship.vx + ship.speed * dt_factor);
    } else {
      ship.vx *= Math.pow(ship.friction, dt_factor);
      if (Math.abs(ship.vx) < 0.05) ship.vx = 0;
    }

    ship.x += ship.vx * dt_factor;
    ship.x = Math.max(10, Math.min(CANVAS_W - ship.w - 10, ship.x));

    ship.cooldown = Math.max(0, ship.cooldown - dt);
    if (shoot && ship.cooldown <= 0) {
      g.bullets.push({
        x: ship.x + ship.w / 2,
        y: ship.y - 8,
        vy: -20,
        life: 400,
      });
      ship.cooldown = 300;
    }

    g.bullets = g.bullets
      .map((b) => ({ ...b, y: b.y + b.vy * dt_factor, life: b.life - dt }))
      .filter((b) => b.life > 0 && b.y > -50);
    g.asteroids = g.asteroids
      .map((a) => ({ ...a, y: a.y + a.vy * dt_factor }))
      .filter((a) => a.y < CANVAS_H + 50);

    // Collision detection with shake effects
    for (let i = g.asteroids.length - 1; i >= 0; i--) {
      const a = g.asteroids[i];

      // Bullet collision
      for (let j = g.bullets.length - 1; j >= 0; j--) {
        const b = g.bullets[j];
        const dx = a.x + a.size / 2 - b.x;
        const dy = a.y + a.size / 2 - b.y;
        if (Math.hypot(dx, dy) < a.size / 2 + 6) {
          g.asteroids.splice(i, 1);
          g.bullets.splice(j, 1);
          setScore((s) => s + 10);
          // Small shake when shooting asteroids
          triggerShake(3, 100);
          break;
        }
      }

      if (i >= g.asteroids.length) continue;

      // Ship collision
      const sx = a.x + a.size / 2 - (ship.x + ship.w / 2);
      const sy = a.y + a.size / 2 - (ship.y + ship.h / 2);
      if (Math.hypot(sx, sy) < a.size / 2 + Math.max(ship.w, ship.h) / 2 - 6) {
        g.asteroids.splice(i, 1);
        setLives((l) => {
          const nl = l - 1;
          if (nl <= 0) {
            runningRef.current = false;
            setRunning(false);
            g.gameOver = true;
            setStatus("Game Over – Click Start");
          }
          return nl;
        });
        // Big shake when hit by asteroid
        triggerShake(15, 300);
      } else if (a.y > CANVAS_H + 50) {
        g.asteroids.splice(i, 1);
        setScore((s) => Math.max(0, s - 2));
      }
    }
  }

  /* ---------------------- Drawing Functions ---------------------- */
  function draw() {
    const ctx = gameCanvasRef.current?.getContext("2d");
    if (!ctx) return;

    ctx.save();

    // Apply shake transform
    ctx.translate(gameRef.current.shake.x, gameRef.current.shake.y);

    ctx.clearRect(-20, -20, CANVAS_W + 40, CANVAS_H + 40);
    ctx.fillStyle = "#051526";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const g = gameRef.current;
    const ship = g.ship;
    const ges = gestureRef.current;

    // Ship Drawing
    ctx.save();
    ctx.translate(ship.x, ship.y);

    if (ges.boost) {
      ctx.fillStyle = "#ff8b60";
      ctx.beginPath();
      ctx.ellipse(
        ship.w / 2,
        ship.h + 6,
        10 + Math.random() * 4,
        22 + Math.random() * 6,
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    ctx.fillStyle = "#7ee787";
    ctx.beginPath();
    ctx.moveTo(ship.w / 2, 0);
    ctx.lineTo(ship.w, ship.h);
    ctx.lineTo(0, ship.h);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#022b32";
    ctx.beginPath();
    ctx.arc(ship.w / 2, ship.h * 0.6, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Bullets & Asteroids
    ctx.fillStyle = "#fff584";
    g.bullets.forEach((b) => ctx.fillRect(b.x - 2, b.y - 6, 4, 12));

    ctx.fillStyle = "#ff7a7a";
    g.asteroids.forEach((a) => {
      ctx.beginPath();
      ctx.ellipse(
        a.x + a.size / 2,
        a.y + a.size / 2,
        a.size / 2,
        a.size / 2,
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();
    });

    // Hand indicators
    if (ges.moveLeft) {
      ctx.fillStyle = "rgba(160,200,255,.9)";
      ctx.beginPath();
      ctx.arc(CANVAS_W - 60, 40, 10, 0, Math.PI * 2);
      ctx.fill();
    }
    if (ges.moveRight) {
      ctx.fillStyle = "rgba(160,255,200,.9)";
      ctx.beginPath();
      ctx.arc(CANVAS_W - 30, 40, 10, 0, Math.PI * 2);
      ctx.fill();
    }
    if (ges.shoot) {
      ctx.fillStyle = "rgba(255, 60, 60, 1)";
      ctx.beginPath();
      ctx.arc(CANVAS_W - 45, 70, 8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Game Over screen
    if (g.gameOver) {
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = "#bcb1b1ff";
      ctx.font = "bold 64px Arial";
      ctx.textAlign = "center";
      ctx.fillText("GAME OVER", CANVAS_W / 2, CANVAS_H / 6);

      // ctx.fillStyle = "#fff";
      // ctx.font = "bold 32px Arial";
      // ctx.fillText(`Final Score: ${score}`, CANVAS_W / 2, CANVAS_H / 2 - 60);

      ctx.font = "24px Arial";
      ctx.fillText(
        "Click Start to play again",
        CANVAS_W / 2,
        CANVAS_H / 2 - 30
      );
    }

    ctx.restore();
  }

  function gameLoop(time) {
    const dt = time - (gameLoop.last || time);
    gameLoop.last = time;

    if (videoRef.current && videoRef.current.readyState >= 2) {
      detectHandGestures(videoRef.current);
    }

    drawCameraPreview();

    if (runningRef.current) {
      step(dt);
    }

    // Always draw
    draw();

    // Keep loop running if game is active OR game over (to show Game Over screen)
    if (runningRef.current || gameRef.current.gameOver) {
      rafRef.current = requestAnimationFrame(gameLoop);
    }
  }

  function drawCameraPreview() {
    if (
      !camCanvasRef.current ||
      !videoRef.current ||
      videoRef.current.readyState < 2
    ) {
      return;
    }

    try {
      const ctx = camCanvasRef.current.getContext("2d");
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, CAM_W, CAM_H);

      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(videoRef.current, -CAM_W, 0, CAM_W, CAM_H);
      ctx.restore();

      const ges = gestureRef.current;

      ctx.strokeStyle = "rgba(100, 200, 255, 0.5)";
      ctx.lineWidth = 2;

      ctx.strokeRect(0, 0, CAM_W / 3, CAM_H);
      ctx.fillStyle = "rgba(100, 200, 255, 0.3)";
      if (ges.moveLeft) {
        ctx.fillRect(0, 0, CAM_W / 3, CAM_H);
      }

      ctx.strokeRect((2 * CAM_W) / 3, 0, CAM_W / 3, CAM_H);
      ctx.fillStyle = "rgba(100, 255, 200, 0.3)";
      if (ges.moveRight) {
        ctx.fillRect((2 * CAM_W) / 3, 0, CAM_W / 3, CAM_H);
      }

      if (ges.shoot) {
        ctx.strokeStyle = "rgba(255, 60, 60, 1)";
        ctx.lineWidth = 4;
        ctx.strokeRect(0, 0, CAM_W, CAM_H);
      }

      ctx.fillStyle = "#fff";
      ctx.font = "bold 10px Arial";
      ctx.fillText("LEFT", 5, 15);
      ctx.fillText("RIGHT", (2 * CAM_W) / 3 + 5, 15);
    } catch (e) {
      console.error("Error drawing video:", e);
    }
  }

  function resetGameState() {
    setScore(0);
    setLives(3);

    gameRef.current = {
      width: CANVAS_W,
      height: CANVAS_H,
      ship: {
        x: CANVAS_W / 2,
        y: CANVAS_H - 80,
        w: 44,
        h: 28,
        vx: 0,
        vy: 0,
        speed: 1.0,
        maxSpeed: 12,
        friction: 0.95,
        cooldown: 0,
      },
      bullets: [],
      asteroids: [],
      lastAsteroidAt: 0,
      time: 0,
      shake: { x: 0, y: 0, intensity: 0, duration: 0 },
      gameOver: false,
    };
    drawInitialState();
  }

  async function startGame() {
    if (runningRef.current) return;

    resetGameState();
    setStatus("Requesting camera access...");
    setDebugInfo("Initializing...");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
      });

      streamRef.current = stream;
      setStatus("Camera acquired, initializing video...");

      if (!videoRef.current) {
        videoRef.current = document.createElement("video");
      }

      videoRef.current.playsInline = true;
      videoRef.current.muted = true;
      videoRef.current.autoplay = true;
      videoRef.current.srcObject = stream;

      setStatus("Waiting for video...");

      await new Promise((resolve) => {
        let resolved = false;

        const checkReady = () => {
          if (resolved) return;
          if (videoRef.current.readyState >= 2) {
            resolved = true;
            resolve();
          }
        };

        const events = ["loadedmetadata", "loadeddata", "canplay"];
        events.forEach((event) =>
          videoRef.current.addEventListener(event, checkReady)
        );

        const interval = setInterval(checkReady, 100);

        setTimeout(() => {
          clearInterval(interval);
          events.forEach((event) =>
            videoRef.current.removeEventListener(event, checkReady)
          );
          if (!resolved) {
            resolved = true;
            resolve();
          }
        }, 5000);
      });

      setStatus("Starting video...");
      try {
        await videoRef.current.play();
      } catch (e) {
        console.log("Play error (might be ok):", e);
      }

      setStatus("Starting preview...");

      await new Promise((resolve) => setTimeout(resolve, 500));

      setStatus("Starting game...");

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      runningRef.current = true;
      setRunning(true);

      draw();

      gameLoop.last = performance.now();
      rafRef.current = requestAnimationFrame(gameLoop);

      setStatus("🚀 Playing! Wave hands to play.");
    } catch (e) {
      console.error("❌ Camera startup error:", e);
      setStatus(`Camera error: ${e.message}`);
      setDebugInfo(`Error: ${e.message}`);
    }
  }

  function stopGame() {
    runningRef.current = false;
    setRunning(false);

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (camRafRef.current) {
      cancelAnimationFrame(camRafRef.current);
      camRafRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStatus("Stopped – Click Start to play again");
    setDebugInfo("");
    drawInitialState();
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#4d525d" }}>
      <div
        style={{
          width: 320,
          background: "#1f1f1f",
          padding: 16,
          color: "#fff",
        }}
      >
        <Text
          style={{
            color: "#e6eef8",
            marginBottom: 8,
            fontSize: 13,
            fontWeight: "bold",
            display: "block",
          }}
        >
          {status}
        </Text>

        <Divider
          style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.2)" }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <Button
            onClick={startGame}
            disabled={running}
            type="primary"
            block
            size="large"
            style={{
              background: running ? "#555" : "#1890ff",
              borderColor: running ? "#555" : "#1890ff",
            }}
          >
            Start Game
          </Button>
          <Button
            onClick={stopGame}
            disabled={!running}
            danger
            block
            size="large"
            style={{
              background: !running ? "#555" : "#ff4d4f",
              borderColor: !running ? "#555" : "#ff4d4f",
              color: "#fff",
            }}
          >
            Stop Game
          </Button>
          <Button
            onClick={resetGameState}
            type="primary"
            block
            ghost
            size="large"
          >
            Reset
          </Button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text
            style={{
              color: "#4ade80",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            Score: {score}
          </Text>
          <Text
            style={{
              color: "#f87171",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            Lives: {lives}
          </Text>
          <Text
            style={{
              color: "#fff",
              fontSize: 22,
              fontWeight: "bold",
              display: "block",
            }}
          >
            Level: {Math.floor(score / 50)}
          </Text>
        </div>

        <Divider
          style={{ margin: "16px 0", borderColor: "rgba(255,255,255,0.2)" }}
        />

        <div
          style={{
            color: "#bfe7ff",
            fontSize: 11,
            marginBottom: 12,
            lineHeight: 1.6,
          }}
        >
          <Title level={4} style={{ color: "#bfe7ff" }}>
            Controls:
          </Title>
          <br />
          <Text style={{ color: "#bfe7ff" }}>Wave left hand to move left.</Text>
          <br />
          <Text style={{ color: "#bfe7ff" }}>
            Wave right hand to move right.
          </Text>
          <br />
          <Text style={{ color: "#bfe7ff" }}>
            Difficulty increases every 50 points scored!
          </Text>
        </div>

        <Text
          style={{
            marginBottom: 8,
            color: "#999",
            fontSize: 11,
            fontWeight: "bold",
            display: "block",
          }}
        >
          Camera Preview:
        </Text>
        <canvas
          ref={camCanvasRef}
          width={CAM_W}
          height={CAM_H}
          style={{
            borderRadius: 8,
            width: "100%",
            background: "#000",
            border: gestureRef.current.shoot
              ? "2px solid red"
              : "2px solid #333",
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <canvas
          ref={gameCanvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{ borderRadius: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.6)" }}
        />
      </div>
    </div>
  );
}
